import React, { useMemo, useState, useEffect } from 'react';
import { useNumberLocale, formatFixed } from '../../../utils/numberFormat';
import TailwindPieChart from '../../Tailwind/TailwindPieChart';
import { useChartPalette } from '../../Tailwind/chartColors';

type Metric = 'commitment' | 'distributions';

export default function ConcentrationTab({ data }: { data: any }) {
    const numberLocale = useNumberLocale();
    const palette = useChartPalette();
    const colorAt = (i: number) => palette[i % palette.length];
    const byAsset = data.by_asset || [];
    const byTag = data.by_tag || [];

    const [metric, setMetric] = useState<Metric>('commitment');
    const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());

    useEffect(() => {
        setSelectedTags(new Set(byTag.map((t: any) => t.name)));
    }, [data]);

    const valueKey = metric === 'commitment' ? 'commitment' : 'distributions';
    const pctKey = metric === 'commitment' ? 'commitment_pct' : 'distributions_pct';
    const valueLabel = metric === 'commitment' ? 'Commitment' : 'Distributions';

    const formatPercentLabel = (entry: any) =>
        `${formatFixed(entry.value, numberLocale, 1)}%`;
    const formatPercentTooltip = (value: any) =>
        `${formatFixed(value, numberLocale, 1)}%`;

    const assetPieData = useMemo(() => {
        return byAsset
            .filter((item: any) => (item[valueKey] || 0) > 0)
            .map((item: any, i: number) => ({
                name: item.name,
                value: item[pctKey],
                raw: item[valueKey],
                color: palette[i % palette.length],
            }));
    }, [byAsset, valueKey, pctKey, palette]);

    const filteredByTag = useMemo(() => {
        return byTag.filter((t: any) => selectedTags.has(t.name) && (t[valueKey] || 0) > 0);
    }, [byTag, selectedTags, valueKey]);

    // Recompute % so the filtered subset sums to 100
    const tagPieData = useMemo(() => {
        const subTotal = filteredByTag.reduce((acc: number, t: any) => acc + (t[valueKey] || 0), 0);
        return filteredByTag.map((item: any, i: number) => ({
            name: item.name,
            value: subTotal > 0 ? ((item[valueKey] || 0) / subTotal) * 100 : 0,
            raw: item[valueKey],
            color: item.color || palette[i % palette.length],
        }));
    }, [filteredByTag, valueKey, palette]);

    const toggleTag = (name: string) => {
        setSelectedTags(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    const allTagsSelected = byTag.length > 0 && selectedTags.size === byTag.length;
    const toggleAllTags = () => {
        setSelectedTags(allTagsSelected ? new Set() : new Set(byTag.map((t: any) => t.name)));
    };

    return (
        <div className="space-y-6">
            {/* Metric selector */}
            {(byAsset.length > 0 || byTag.length > 0) && (
                <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-700">Metric:</span>
                    <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-white">
                        {(['commitment', 'distributions'] as Metric[]).map(m => (
                            <button
                                key={m}
                                onClick={() => setMetric(m)}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                    metric === m
                                        ? 'bg-primary text-white'
                                        : 'text-slate-600 hover:bg-slate-100'
                                }`}
                            >
                                {m === 'commitment' ? 'Commitment' : 'Distributions'}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* By Asset */}
            {assetPieData.length > 0 && (
                <section className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Allocation by Asset — {valueLabel}</h3>
                    <div className="flex flex-col lg:flex-row gap-6">
                        <div className="flex-1 min-w-0">
                            <TailwindPieChart
                                data={assetPieData}
                                height={340}
                                innerRadius="40%"
                                outerRadius="70%"
                                paddingAngle={2}
                                margin={{ top: 20, right: 60, bottom: 20, left: 60 }}
                                labelFormatter={formatPercentLabel}
                                labelLine
                                tooltipValueFormatter={formatPercentTooltip}
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr>
                                        <th className="text-left px-4 py-2.5 font-medium text-slate-600">Asset</th>
                                        <th className="text-right px-4 py-2.5 font-medium text-slate-600">{valueLabel}</th>
                                        <th className="text-right px-4 py-2.5 font-medium text-slate-600">%</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {byAsset.map((item: any, i: number) => (
                                        <tr key={i} className="border-b border-slate-100 last:border-b-0">
                                            <td className="px-4 py-2 flex items-center gap-2">
                                                <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: colorAt(i) }} />
                                                <span className="text-slate-900 truncate">{item.name}</span>
                                            </td>
                                            <td className="px-4 py-2 text-right text-slate-700">
                                                {item[valueKey]?.toLocaleString(numberLocale, { maximumFractionDigits: 0 })}
                                            </td>
                                            <td className="px-4 py-2 text-right font-medium text-slate-900">
                                                {item[pctKey] != null ? formatFixed(item[pctKey], numberLocale, 1) : ''}%
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            )}

            {/* By Tag */}
            {byTag.length > 0 && (
                <section className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-slate-900">Allocation by Tag — {valueLabel}</h3>
                        <button
                            onClick={toggleAllTags}
                            className="text-xs font-medium text-primary hover:opacity-80"
                        >
                            {allTagsSelected ? 'Clear all' : 'Select all'}
                        </button>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                        {byTag.map((tag: any, i: number) => {
                            const checked = selectedTags.has(tag.name);
                            const color = tag.color || colorAt(i);
                            return (
                                <button
                                    key={tag.name}
                                    onClick={() => toggleTag(tag.name)}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                                        checked
                                            ? 'border-slate-300 bg-white text-slate-700'
                                            : 'border-slate-200 bg-slate-50 text-slate-400 line-through'
                                    }`}
                                >
                                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color, opacity: checked ? 1 : 0.3 }} />
                                    {tag.name}
                                </button>
                            );
                        })}
                    </div>

                    {tagPieData.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-8">No tag selected</p>
                    ) : (
                        <div className="flex flex-col lg:flex-row gap-6">
                            <div className="flex-1 min-w-0">
                                <TailwindPieChart
                                    data={tagPieData}
                                    height={340}
                                    innerRadius="40%"
                                    outerRadius="70%"
                                    paddingAngle={2}
                                    margin={{ top: 20, right: 60, bottom: 20, left: 60 }}
                                    labelFormatter={formatPercentLabel}
                                    labelLine
                                    tooltipValueFormatter={formatPercentTooltip}
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr>
                                            <th className="text-left px-4 py-2.5 font-medium text-slate-600">Tag</th>
                                            <th className="text-right px-4 py-2.5 font-medium text-slate-600">{valueLabel}</th>
                                            <th className="text-right px-4 py-2.5 font-medium text-slate-600">%</th>
                                            <th className="text-right px-4 py-2.5 font-medium text-slate-600">Assets</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredByTag.map((item: any, i: number) => {
                                            const subTotal = filteredByTag.reduce((acc: number, t: any) => acc + (t[valueKey] || 0), 0);
                                            const pct = subTotal > 0 ? ((item[valueKey] || 0) / subTotal) * 100 : 0;
                                            return (
                                                <tr key={i} className="border-b border-slate-100 last:border-b-0">
                                                    <td className="px-4 py-2 flex items-center gap-2">
                                                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: item.color || colorAt(i) }} />
                                                        <span className="text-slate-900">{item.name}</span>
                                                    </td>
                                                    <td className="px-4 py-2 text-right text-slate-700">
                                                        {item[valueKey]?.toLocaleString(numberLocale, { maximumFractionDigits: 0 })}
                                                    </td>
                                                    <td className="px-4 py-2 text-right font-medium text-slate-900">
                                                        {formatFixed(pct, numberLocale, 1)}%
                                                    </td>
                                                    <td className="px-4 py-2 text-right text-slate-700">{item.count}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </section>
            )}

            {/* Empty state */}
            {byAsset.length === 0 && byTag.length === 0 && (
                <div className="text-center py-16 text-slate-400">
                    <span className="material-symbols-outlined text-[48px] block mb-3">pie_chart</span>
                    <p className="text-base font-medium">No concentration data</p>
                    <p className="text-sm mt-1">Add assets and compute the portfolio to see allocation breakdown</p>
                </div>
            )}
        </div>
    );
}
